import styled, { css } from 'styled-components';

import Richtext from 'approot/shared/richtext/richtext';
import { grey1, grey10, purple7 } from 'approot/shared/styles/colours.style';
import { rem } from 'approot/shared/style';
import {
  baseBoxShadowFocus,
  borderRadius16,
  borderRadius8,
} from 'approot/shared/styles/borders.style';
import { spacing20, spacing12 } from 'approot/shared/styles/spacing.style';
import {
  ARROW_ALIGNMENT_BOTTOM,
  ARROW_ALIGNMENT_CENTRE,
  ARROW_ALIGNMENT_LEFT,
  ARROW_ALIGNMENT_RIGHT,
  ARROW_ALIGNMENT_TOP,
  POSITION_BOTTOM,
  POSITION_LEFT,
  POSITION_RIGHT,
  POSITION_TOP,
  BORDER_STYLE_BOX,
  BORDER_STYLE_SPEECH,
  ArrowAlignment,
  ActivityPosition,
  BorderStyle,
} from 'global/constants';
import {
  RichtextProps,
  richtextCommonCss,
} from 'approot/shared/richtext/richtext.style';
import { hexColourToRGBArray } from 'lib/strings/strings.colour';
import { InquisitiveContentFlexAlignment } from 'global/graphql/scalars.type';

export type InfoBoxProps = {
  $colour: string;
  $showBackgroundColour?: boolean;
  $borderStyle?: BorderStyle;
  $alignment?: ArrowAlignment;
  $width?: number | null;
  $textAlignment?: InquisitiveContentFlexAlignment | undefined | null;
  $position?: ActivityPosition;
  $isZoomed?: boolean;
};

export const boxShadowInfoBoxStandard = '3px 4px 4px 0 rgb(22 34 51 / 24%)';

export const boxShadowSpeech = `2px 2px 0px 0px rgba(${hexColourToRGBArray(
  purple7
).join(',')}, 0.25)`;

export const boxShadowSpeechHover = `8px 8px 0px 0px rgba(${hexColourToRGBArray(
  purple7
).join(',')}, 0.25)`;

export const baseInfoBoxSpeech = css<InfoBoxProps>`
  background-color: ${grey1};
  border-width: 2px;
  border-style: solid;
  border-color: ${({ $colour = purple7 }) => $colour};
  border-radius: ${rem(borderRadius16)};
  color: ${grey10};
  padding: ${rem(spacing12)};
  position: relative;
`;

const baseInfoBoxSpeechCss = css<InfoBoxProps>`
  ${baseInfoBoxSpeech}

  ${({ $alignment, $position, $colour = purple7 }) =>
    $alignment &&
    css`
      &:after {
        content: '';
        position: absolute;
        background-color: ${grey1};
        border-bottom: 2px solid ${$colour};
        border-right: 2px solid ${$colour};
        border-top: 2px solid ${grey1};
        border-left: 2px solid ${grey1};
        border-radius: ${rem(3)};
        height: ${rem(20)};
        width: ${rem(20)};

        ${$alignment === ARROW_ALIGNMENT_LEFT &&
        `
          left: ${rem(30)};
          right: auto;
        `}

        ${$alignment === ARROW_ALIGNMENT_RIGHT &&
        `
          right: ${rem(30)};
          left: auto;
        `}

        ${$alignment === ARROW_ALIGNMENT_CENTRE &&
        `
          left: calc(50% - ${rem(10)});
          right: auto;
        `}

        ${$alignment === ARROW_ALIGNMENT_CENTRE &&
        ($position === POSITION_TOP || $position === POSITION_BOTTOM) &&
        `
          left: calc(50% - ${rem(10)});
          right: auto;
        `}

        ${$alignment === ARROW_ALIGNMENT_CENTRE &&
        ($position === POSITION_LEFT || $position === POSITION_RIGHT) &&
        `
          top: calc(50% - ${rem(10)});
        `}

        ${$alignment === ARROW_ALIGNMENT_TOP &&
        `
          top: ${rem(spacing20)};
          bottom: auto;
        `}

        ${$alignment === ARROW_ALIGNMENT_BOTTOM &&
        `
          bottom: ${rem(spacing20)};
          top: auto;
        `}

        ${$position === POSITION_TOP &&
        `
          top: -${rem(10)};
          transform: rotate(225deg);
        `}

        ${$position === POSITION_BOTTOM &&
        `
          top: calc(100% - ${rem(10)});
          transform: rotate(45deg);
        `}

        ${$position === POSITION_LEFT &&
        `
          left: -${rem(10)};
          transform: rotate(135deg);
        `}

        ${$position === POSITION_RIGHT &&
        `
          right: -${rem(10)};
          left: auto;
          transform: rotate(315deg);
        `}
      }
    `}
`;

export const InfoBoxSpeech = styled(Richtext)<InfoBoxProps>`
  display: flex;
  justify-content: ${({ $textAlignment }) => $textAlignment};

  .ProseMirror {
    ${baseInfoBoxSpeechCss}

    background-color: ${grey1};
    max-width: ${({ $width }) => $width || 100}%;
    width: 100%;

    &:hover,
    &:focus {
      border-width: 2px;
    }

    &:hover {
      border: 2px solid ${purple7};
    }

    &:focus {
      border: 2px solid ${grey10};
    }

    ${({ $borderStyle }) =>
      $borderStyle === BORDER_STYLE_BOX &&
      css`
        border-radius: ${rem(borderRadius8)};
        &:focus {
          box-shadow: ${baseBoxShadowFocus}, ${boxShadowSpeech};
        }
      `};

    ${({ $borderStyle, $colour = purple7 }) =>
      $borderStyle === BORDER_STYLE_SPEECH &&
      css`
        &:hover {
          &:after {
            border-bottom: 2px solid ${$colour};
            border-right: 2px solid ${$colour};
          }
        }
        &:focus {
          box-shadow: ${baseBoxShadowFocus}, ${boxShadowSpeech};
          &:after {
            border-bottom: 2px solid ${grey10};
            border-right: 2px solid ${grey10};
            box-shadow: 2px 2px 0 ${grey10};
          }
        }
      `};
  }
`;

export const InfoBoxSpeechView = styled.div.attrs<InfoBoxProps & RichtextProps>(
  {
    $isSmall: false,
  }
)`
  ${richtextCommonCss}
  ${baseInfoBoxSpeechCss}

  max-width: ${({ $width }) => $width || 100}%;
  width: 100%;

  ${({ $borderStyle }) =>
    $borderStyle === BORDER_STYLE_BOX &&
    css`
      border-radius: ${rem(borderRadius8)};
    `};
`;

export const InfoBoxSpeechViewInteractive = styled.div.attrs<
  InfoBoxProps & RichtextProps
>({
  $isSmall: false,
})`
  ${richtextCommonCss}
  ${baseInfoBoxSpeechCss}
  
  box-shadow: ${boxShadowSpeech};
  max-width: ${({ $width }) => $width || 100}%;
  width: 100%;

  ${({ $borderStyle }) =>
    $borderStyle === BORDER_STYLE_BOX &&
    css`
      border-radius: ${rem(borderRadius8)};
    `};
`;
